<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import LitepieDatepicker from 'litepie-datepicker-tw3'
import dayjs from 'dayjs'

import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import { extractSurveyIdsFromMessageContent } from '@/components/ui/TextareaWithVars/utils'
import CampaignPreview from '@/components/CampaignPreview.vue'
import CampaignUpsell from '../components/CampaignUpsell.vue'
import { getCampaignPreviewText } from '../utils/campaignMessage'

import type { storeInterface } from '@/types/store'
import type {
  CampaignClientTypes,
  CampaignSelection,
  CampaignTime,
  CampaignType,
  Client,
  Country,
  LoyaltyReward,
  MerchantSmsPrice,
  Survey
} from '@/types/types'
import type { Dayjs } from 'dayjs'

const props = defineProps<{
  isProcessing: boolean
  selected: CampaignSelection
  stores: storeInterface[]
  optinTargets: number | string
  merchantSmsPrices: MerchantSmsPrice[]
  clients: Client[]
  globalCost: number
  countries: Country[]
  messageContent: string
  selectedClientType: CampaignClientTypes
  selectedClient: number
  launchChoice: 'now' | 'later'
  launchDate: Dayjs
  dateCampaign: string
  timeCampaign: CampaignTime
  showVerifyNumber: boolean
  conversionDelay: number
  selectedReward?: number | null
  rewards?: LoyaltyReward[]
  surveys?: Survey[] | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue'): void
  (e: 'onFetchSmsPrices'): void
  (e: 'onTrySms'): void
  (e: 'onVerifyNumber', number: string): void
  (e: 'onCancelVerifyNumber'): void
  (e: 'onSendCode', code: string): void
  (e: 'on-create', launchChoice: 'now' | 'later'): void
}>()

const isStoreSenderNameModel = defineModel<boolean>('isStoreSenderName')
const selectedModel = useVModel(props, 'selected', emit)
const selectedClientTypeModel = useVModel(props, 'selectedClientType', emit)
const selectedClientModel = useVModel(props, 'selectedClient', emit)
const launchChoiceModel = useVModel(props, 'launchChoice', emit)
const launchDateModel = useVModel(props, 'launchDate', emit)
const dateCampaignModel = useVModel(props, 'dateCampaign', emit)
const timeCampaignModel = useVModel(props, 'timeCampaign', emit)
const conversionDelayModel = useVModel(props, 'conversionDelay', emit)

const { user } = useLocalStorage()
const { t } = useI18n()
const { formatDate } = useDate()
const { create } = useNotification()
const route = useRoute()
const { send } = useEvent()
const { pad } = useText()
const { hasModuleBySlug } = useAccess()

const testPhoneNumber = ref(user.value.formattedPhone ? user.value.formattedPhone.replaceAll(' ', '') : '')
const isProcessing = toRef(props, 'isProcessing')
const isPhoneVerified = ref(user.value.isPhoneConfirmed)

const otpCode = ref('')
const confirmationLength = 6
const isVerifyNumberShown = ref(props.showVerifyNumber)

const selectedSenderModel = ref<RadioCardOption>(null)

const clientTypes: CampaignClientTypes[] = [
  {
    type: 'each',
    title: t('pages.campaign.campaignCreatePage.block.billing.clientRadios.each.title'),
    description: t('pages.campaign.campaignCreatePage.block.billing.clientRadios.each.description')
  },
  {
    type: 'specific',
    title: t('pages.campaign.campaignCreatePage.block.billing.clientRadios.specific.title'),
    description: t('pages.campaign.campaignCreatePage.block.billing.clientRadios.specific.description')
  }
]

const merchantSmsName = computed(() => user.value.merchant.smsName)

const campaignType = computed<CampaignType>(() => (route.query.type as CampaignType) || 'sms')

const smsNames = computed<string[]>(() => {
  const names = props.selected.stores
    .filter((item) => item.smsName && item.smsName.length > 0)
    .map((item) => item.smsName)

  return names
})

const senderNames = computed<(RadioCardOption & { name: string })[]>(() => {
  const firstSelectedStore = smsNames.value[0]

  const merchantSender: RadioCardOption & { name: string } = {
    title: t('pages.campaign.campaignCreatePage.block.sender.merchantSender.title', {
      merchantName: `(${merchantSmsName.value})`
    }),
    name: merchantSmsName.value,
    description: t('pages.campaign.campaignCreatePage.block.sender.merchantSender.description'),
    value: false
  }

  const storeSender: RadioCardOption & { name: string } = {
    title: t('pages.campaign.campaignCreatePage.block.sender.storeSender.title', {
      storeName: smsNames.value.length === 1 ? `(${firstSelectedStore})` : ''
    }),
    name: firstSelectedStore,
    description: t('pages.campaign.campaignCreatePage.block.sender.storeSender.description'),
    value: true
  }

  return [merchantSender, storeSender]
})

const showSenderBlock = computed(() => {
  const storesHasCustomSenderNames = smsNames.value.some(
    (name) => name.toLowerCase().trim() !== merchantSmsName.value.toLowerCase().trim()
  )
  const storesLength = props.selected.stores.length
  const isSmsNameNeverNull = smsNames.value?.length === storesLength

  return isSmsNameNeverNull && storesHasCustomSenderNames
})

const previewSenderName = computed(() => {
  if (selectedSenderModel.value == null) {
    return merchantSmsName.value
  }

  const storeSenderName = smsNames.value.length === 1 ? senderNames.value[1]?.name : ''
  return selectedSenderModel.value.value === true ? storeSenderName : merchantSmsName.value
})

const isDisabled = computed(() => otpCode.value.length !== confirmationLength)

const isPricingCalculationLoaded = computed(() => {
  return props.optinTargets !== 'loading' && !isProcessing.value
})

const mustSpecifyMandate = computed(() => {
  if (props.clients.length <= 1) {
    return false
  }

  if (campaignType.value !== 'sms') {
    return false
  }

  return user.value?.roleTemplate.slug === 'direction'
})

const selectClient = (id: number) => {
  selectedClientModel.value = id
}

const dropdownClients = computed(() => {
  return props.clients.map((client) => ({
    id: client.id,
    text: `${client.name} - ${client.registrationNumber}`
  }))
})

const hasClientError = computed(function () {
  if (selectedClientTypeModel.value == null) {
    return true
  }
  return selectedClientTypeModel.value.type !== 'each' && selectedClientModel.value === 0
})

const conversionDelayError = computed<string[] | []>(() =>
  conversionDelayModel.value < 3 || conversionDelayModel.value > 30
    ? [t('pages.campaign.campaignCreatePage.notifications.create.conversionDelayError')]
    : []
)

const launchChoices = computed(() => [
  { id: 'now', title: t('pages.campaign.campaignCreatePage.radios.now') },
  { id: 'later', title: t('pages.campaign.campaignCreatePage.radios.later') }
])

const selectedLaunch = computed({
  get: () => launchChoices.value.find((c) => c.id === launchChoiceModel.value),
  set: (opt) => (launchChoiceModel.value = opt?.id as string)
})

const messageSurvey = computed(() => {
  const surveys = props.surveys ?? []
  if (!surveys.length) return null

  const surveyIds = extractSurveyIdsFromMessageContent(props.messageContent ?? '')
  const firstId = surveyIds[0]
  if (firstId == null || Number.isNaN(firstId)) return null

  return surveys.find((survey) => survey.id === firstId) ?? null
})

const disableDate = (date: Date) => {
  const d = dayjs(date).startOf('day')
  const today = dayjs().startOf('day')
  if (d.isBefore(today) || date.getDay() === 0) return true

  const survey = messageSurvey.value
  if (survey) {
    const launchDay = dayjs(survey.launchDate * 1000).startOf('day')
    if (d.isBefore(launchDay)) return true

    if (survey.endDate) {
      const endDay = dayjs(survey.endDate * 1000).startOf('day')
      if (d.isAfter(endDay)) return true
    }
  }

  return false
}

const formatHoursAndMinutes = (number: number) => {
  return pad(number, 2)
}

const nowFormattedDate = dayjs().format('DD/MM/YYYY')

const now = computed(() => new Date())

const isDateSelectedBeforeNow = computed(() => {
  if (launchChoiceModel.value === 'now') {
    return false
  }
  const minutes = now.value.getMinutes()
  const hours = now.value.getHours()
  const checkEqualDate = dateCampaignModel.value === nowFormattedDate
  const checkMinutes = timeCampaignModel.value.minute < minutes
  const checkHours = timeCampaignModel.value.hour < hours || (timeCampaignModel.value.hour === hours && checkMinutes)
  const checkDate = convertDateFormat(dateCampaignModel.value) < convertDateFormat(nowFormattedDate)

  return (checkEqualDate && checkHours) || checkDate
})

const convertDateFormat = (date: string) => {
  return formatDate({ date: date, initialFormat: 'DD/MM/YYYY', toFormat: 'YYYY-MM-DD' })
}

const confirmButtonText = computed(() => {
  const date = launchDateModel.value !== null ? launchDateModel.value.format('LLL') : ''
  return date
    ? t('pages.campaign.campaignCreatePage.confirm.modal.text') + date
    : t('pages.campaign.campaignCreatePage.confirm.modal.textNow')
})

const calculateLaunchDate = () => {
  let launchDate = null
  const isNow = launchChoiceModel.value === 'now'

  if (!isNow) {
    if (dateCampaignModel.value === undefined) {
      create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.create.noDateSelected'),
        timeout: 2000
      })
    } else {
      // Set hour to prevent UTC to get the day before with its -2 hours
      launchDate = dayjs(`${dateCampaignModel.value} 12:00`, 'DD/MM/YYYY HH:ii', 'fr').utc()
      launchDate = launchDate.hour(timeCampaignModel.value.hour).minute(timeCampaignModel.value.minute)
    }
  }

  launchDateModel.value = launchDate
}

const templateId = computed(() => route.query?.templateId)

const handleCreation = () => {
  emit('on-create', launchChoiceModel.value)

  if (templateId.value) {
    send('segment', 'campaign.template:create:clicked', {
      templateId: templateId.value,
      merchantId: user.value.merchant.publicId,
      module: 'campaign'
    })
  }
}

const handleSubmitCode = () => {
  emit('onSendCode', otpCode.value)
}

const informationText = computed(() => {
  return hasModuleBySlug('pack-sms')
    ? t('pages.campaign.campaignCreatePage.block.validationSmsPackSubscribers.information')
    : t('pages.campaign.campaignCreatePage.block.validationInformation.text', { globalCost: props.globalCost })
})

const isMessageSurveyInRange = computed(() => {
  if (!messageSurvey.value) return true
  const launchDate = launchChoiceModel.value === 'now' ? dayjs() : convertDateFormat(dateCampaignModel.value)

  const isAfterLaunchDate = dayjs(messageSurvey.value?.launchDate * 1000)
    .startOf('day')
    .isSameOrBefore(dayjs(launchDate))

  const isBeforeEndDate = messageSurvey.value?.endDate
    ? dayjs(messageSurvey.value?.endDate * 1000)
        .endOf('day')
        .isSameOrAfter(dayjs(launchDate))
    : true

  return isAfterLaunchDate && isBeforeEndDate
})

const launchDisabled = computed(
  () =>
    hasClientError.value ||
    isDateSelectedBeforeNow.value ||
    conversionDelayError.value.length > 0 ||
    isProcessing.value ||
    !isMessageSurveyInRange.value
)

const updateSenderName = (value: RadioCardOption & { name: string }) => {
  selectedSenderModel.value = value
  isStoreSenderNameModel.value = value.value as boolean
}

const longestStoreName = computed(() => {
  return props.stores?.reduce((longest, current) => (current.label.length > longest.label.length ? current : longest), {
    label: ''
  }).label
})

const previewMessage = computed(() => {
  return getCampaignPreviewText(
    props.messageContent,
    longestStoreName.value,
    props.rewards?.find((r) => r.id === props.selectedReward)
  )
})

watch(
  () => props.showVerifyNumber,
  () => (isVerifyNumberShown.value = props.showVerifyNumber)
)
watch(
  () => user.value.isPhoneConfirmed,
  () => (isPhoneVerified.value = user.value.isPhoneConfirmed)
)
watch(
  () => props.isProcessing,
  () => (isProcessing.value = props.isProcessing)
)

onMounted(() => {
  if (!mustSpecifyMandate.value) {
    selectedClientTypeModel.value = clientTypes[0]
  }

  selectedSenderModel.value = isStoreSenderNameModel.value === true ? senderNames.value[1] : senderNames.value[0]
})
</script>
<template>
  <section class="space-y-6">
    <div class="rounded-md border bg-white">
      <div class="border-b px-4 py-5 sm:px-6">
        <h3 class="mt-2 flex text-lg font-medium leading-6 text-foreground">
          {{ t('pages.campaign.campaignCreatePage.block.conversion.title') }}
        </h3>
        <p class="mt-1 text-sm text-muted-foreground">
          {{ t('pages.campaign.campaignCreatePage.block.conversion.description') }}
        </p>
      </div>
      <div class="w-full px-4 py-5 sm:px-6">
        <AppFormInput
          id="conversionDelay"
          v-model.number="conversionDelayModel"
          :label="t('pages.campaign.campaignCreatePage.block.conversion.label')"
          type="number"
          placeholder="7"
          :max="30"
          :errors="conversionDelayError"
          class="w-full md:w-1/2"
        />

        <BaseTip
          v-if="selectedReward"
          severity="info"
          class="mt-4 w-full"
          :text="t('pages.campaign.campaignCreatePage.block.conversion.infoConversionDelay')"
        />
      </div>
    </div>
    <template v-if="campaignType === 'sms'">
      <div v-if="showSenderBlock">
        <div class="rounded-md border bg-white">
          <div class="border-b px-4 py-5 sm:px-6">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="mt-2">
                <h3 class="flex text-lg font-medium leading-6 text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.block.sender.title') }}
                </h3>
              </div>
            </div>
          </div>
          <div v-if="selectedSenderModel" class="px-4 py-5 sm:px-6">
            <AppRadioCard
              :model-value="selectedSenderModel"
              class="grid gap-4 sm:grid-cols-2"
              :options="senderNames"
              :is-only-image="false"
              @update:model-value="updateSenderName"
            />
          </div>
        </div>
      </div>
      <div class="relative">
        <div class="relative grid rounded-md border bg-white sm:grid-cols-2">
          <div class="col-span-full row-start-1 border-b px-4 py-5 sm:px-6">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="mt-2">
                <h3 class="text-lg font-medium leading-6 text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.block.preview.title') }}
                </h3>
              </div>
            </div>
          </div>
          <div class="relative row-start-3 h-72 overflow-hidden sm:row-start-2">
            <CampaignPreview
              :name="previewSenderName"
              :message="previewMessage"
              :raw-message="messageContent"
              :reward-name="rewards?.find((r) => r.id === selectedReward)?.name"
              type="sms"
              class="h-full"
            />
          </div>
          <div class="p-4">
            <template v-if="isPhoneVerified">
              <p class="mb-6 mt-1 max-w-2xl text-xs text-muted-foreground">
                {{ t('pages.campaign.campaignCreatePage.block.preview.description.launch') }}
              </p>
              <div class="flex items-center justify-between rounded-md border bg-white px-3 py-2">
                <div class="mx-2 flex items-center text-sm lg:mx-5">
                  {{ testPhoneNumber }} <BaseIcon name="HeroCheckIconSolid" class="h-5 w-5 text-green-500" />
                </div>
                <Button variant="ghost" @click="emit('onTrySms')">
                  <span class="hidden lg:block">{{
                    t('pages.campaign.campaignCreatePage.block.trySms.testCampaign')
                  }}</span>
                  <span class="block lg:hidden">{{ t('pages.campaign.campaignCreatePage.block.trySms.test') }}</span>
                </Button>
              </div>
            </template>
            <template v-else-if="!isVerifyNumberShown">
              <div class="relative flex h-full flex-col">
                <p class="mt-1 max-w-2xl text-sm text-muted-foreground lg:text-xs">
                  {{ t('pages.campaign.campaignCreatePage.block.preview.description.toConfirm') }}
                </p>
                <div class="mt-4 flex flex-col items-center gap-3 lg:flex-row lg:gap-0">
                  <CountryDropdown v-if="countries" v-model="selectedModel.countryAlpha2Code" :countries="countries" />
                  <AppFormInput id="testPhoneNumber" v-model="testPhoneNumber" placeholder="0612345896">
                    <template #icon-right>
                      <Spinner v-if="isProcessing" />
                      <span
                        v-else
                        class="cursor-pointer text-sm font-semibold text-foreground lg:text-xs"
                        @click="emit('onVerifyNumber', testPhoneNumber)"
                      >
                        {{ t('pages.campaign.campaignCreatePage.block.trySms.confirmMyNumber') }}</span
                      ></template
                    >
                  </AppFormInput>
                </div>
              </div>
            </template>
            <template v-else>
              <p class="mb-6 mt-1 max-w-2xl text-sm text-muted-foreground lg:text-xs">
                {{ t('pages.campaign.campaignCreatePage.block.preview.description.code', { number: testPhoneNumber }) }}
              </p>
              <InputOTP v-model="otpCode" :maxlength="confirmationLength" inputmode="numeric" class="mb-6">
                <InputOTPGroup>
                  <InputOTPSlot v-for="i in confirmationLength" :key="i" :index="i - 1" />
                </InputOTPGroup>
              </InputOTP>
              <div class="flex items-center gap-5">
                <Button :disabled="isDisabled" variant="outline" class="bg-white" @click="handleSubmitCode">{{
                  t('global.words.submit')
                }}</Button>
                <p class="cursor-pointer text-xs text-primary" @click="emit('onCancelVerifyNumber')">
                  {{ t('pages.campaign.campaignCreatePage.block.trySms.didNotReceive') }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div>
        <div class="rounded-md border bg-white">
          <div class="border-b px-4 py-5 sm:px-6">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="mt-2">
                <h3 class="flex items-center text-lg font-medium leading-6 text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.block.billing.title') }}
                  <AppTooltip width="w-80 z-10" tooltip-position="top" @opened="$emit('onFetchSmsPrices')">
                    <span class="ml-1.5 inline-flex size-4 shrink-0">
                      <BaseIcon name="IconoirInfoCircle" class="size-full cursor-pointer text-foreground-secondary" />
                    </span>
                    <template #tooltip-content>
                      <h2 class="m-2 mb-2 text-left text-lg font-bold text-foreground-disabled">
                        {{ t('pages.campaign.campaignCreatePage.block.billing.tooltip.title') }}
                      </h2>
                      <div class="m-2 flex flex-col gap-3">
                        <p class="text-left text-sm">
                          {{ t('pages.campaign.campaignCreatePage.block.billing.tooltip.description') }}
                        </p>
                      </div>

                      <div v-if="merchantSmsPrices.length === 0" class="m-auto w-min px-4 py-5 sm:px-6">
                        <svg
                          class="mr-3 h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      </div>
                      <div v-else class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-4">
                        <div
                          v-for="price in merchantSmsPrices"
                          :key="price.country.id"
                          class="relative col-span-2 flex items-center space-x-1 rounded-lg border border-border bg-white px-3 py-2"
                        >
                          <div class="shrink-0">
                            <img class="h-5 w-5 rounded-full" :src="price.country.flag" alt="" />
                          </div>
                          <div class="min-w-0 flex-1">
                            <a href="#" class="focus:outline-hidden">
                              <span class="absolute inset-0" aria-hidden="true" />
                              <p class="text-xs font-medium text-foreground">
                                {{ price.country.name }}
                              </p>
                              <p class="truncate text-xs text-muted-foreground">
                                {{ Number(price.sellingSmsPriceWithoutVat) / 100 }}€
                              </p>
                            </a>
                          </div>
                        </div>
                      </div>
                    </template>
                  </AppTooltip>
                </h3>
              </div>
            </div>
          </div>

          <div v-if="!isPricingCalculationLoaded" class="flex px-4 py-5 sm:px-6">
            <svg
              class="mr-3 h-5 w-5 animate-spin text-muted-foreground"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span class="text-xs text-muted-foreground sm:text-sm">
              {{ t('pages.campaign.campaignCreatePage.block.billing.loading') }}
            </span>
          </div>
          <div v-else class="px-4 py-5 sm:px-6">
            <dl class="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-4">
              <div class="sm:col-span-1">
                <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                  {{ t('pages.campaign.campaignCreatePage.block.billing.campaignMessages') }}
                </dt>
                <dd class="mt-1 text-xs text-foreground sm:text-sm">
                  {{ optinTargets }}
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                  {{ t('pages.campaign.campaignCreatePage.block.billing.smsCost.averageTitle') }}
                </dt>
                <dd class="mt-1 text-xs text-foreground sm:text-sm">
                  {{
                    t('pages.campaign.campaignCreatePage.block.billing.smsCost.value', {
                      smsPrice: Number(globalCost / Number(optinTargets)).toPrecision(3)
                    })
                  }}
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                  {{ t('pages.campaign.campaignCreatePage.block.billing.campaignCost') }}
                </dt>
                <dd class="mt-1 text-xs text-foreground sm:text-sm">{{ globalCost }} €</dd>
              </div>
              <BaseTip class="col-span-full" severity="info">
                <template #text>
                  <p v-html="informationText"></p>
                </template>
              </BaseTip>
              <CampaignUpsell :has-image="false" class="col-span-1 sm:col-span-4" />
              <div v-if="mustSpecifyMandate" class="sm:col-span-4">
                <dt class="text-xs font-medium text-muted-foreground sm:text-sm">
                  {{ t('pages.campaign.campaignCreatePage.block.billing.client') }}
                </dt>
                <dd class="mt-1 text-xs text-foreground sm:text-sm">
                  <div class="w-full py-2">
                    <div class="w-full">
                      <AppRadioCard
                        v-model="selectedClientTypeModel"
                        class="grid gap-4 sm:grid-cols-2"
                        :options="clientTypes"
                        :is-only-image="false"
                      />
                    </div>
                  </div>
                </dd>
              </div>
              <div class="sm:col-span-4">
                <dt
                  v-if="selectedClientTypeModel?.type === 'specific'"
                  class="text-xs font-medium text-muted-foreground sm:text-sm"
                >
                  {{ t('pages.campaign.campaignCreatePage.block.billing.clientToBill') }}
                </dt>
                <dd
                  v-if="selectedClientTypeModel?.type === 'specific'"
                  class="mt-2 pl-2 pr-6 text-left text-xs text-foreground sm:text-sm"
                >
                  <ComboboxSelect
                    value-key="id"
                    label-key="text"
                    :placeholder="t('pages.campaign.campaignCreatePage.block.billing.dropdown')"
                    :model-value="selectedClientModel"
                    :options="dropdownClients"
                    trigger-class="w-full"
                    @update:model-value="selectClient($event)"
                  />
                  <div v-if="hasClientError" class="ml-2 mt-1 text-red-500">
                    {{ t('pages.campaign.campaignCreatePage.block.billing.youMustSelectClient') }}
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </template>

    <div class="mt-2 flex flex-col rounded-md border bg-white">
      <div class="border-b px-4 py-5 sm:px-6">
        <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div class="mt-2">
            <h3 class="flex text-lg font-medium leading-6 text-foreground">
              {{ t('pages.campaign.campaignCreatePage.block.launch.title') }}
            </h3>
          </div>
        </div>
      </div>
      <div class="mt-2 grid grid-cols-1 gap-2 p-6 lg:grid-cols-6">
        <BaseTip
          severity="warn"
          icon="HeroExclamationTriangleIconOutline"
          class="col-span-full mb-5"
          :text="t('pages.campaign.campaignCreatePage.block.warningInformation.text')"
        />
        <div class="col-span-1 flex flex-col gap-3 lg:col-span-3">
          <AppRadioCard v-model="selectedLaunch" :options="launchChoices" class="grid grid-cols-2 gap-3" />
          <div v-if="launchChoiceModel === 'later'" class="flex-col items-center justify-between text-center">
            <fieldset class="flex flex-col">
              <div class="mr-3 h-4 w-4"></div>
              <div class="flex flex-col rounded-md text-base text-foreground md:flex-row">
                <div class="flex flex-col text-base text-foreground">
                  <p class="mb-1 text-left text-sm text-foreground">
                    {{ t('pages.campaign.campaignCreatePage.block.launch.date') }}
                  </p>
                  <LitepieDatepicker
                    v-model="dateCampaignModel"
                    :disable-date="disableDate"
                    class="rounded-sm pr-2!"
                    as-single
                    i18n="fr"
                    :formatter="{
                      date: 'DD/MM/YYYY',
                      month: 'MMM'
                    }"
                  />
                </div>
                <div class="my-5 flex flex-col md:my-0 md:ml-2">
                  <p class="mb-1 text-left text-sm text-foreground">
                    {{ t('pages.campaign.campaignCreatePage.block.launch.hour') }}
                  </p>
                  <div class="flex gap-1">
                    <Select v-model="timeCampaignModel.hour">
                      <SelectTrigger class="w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="hour in 24" :key="hour" :value="hour - 1">
                          {{ formatHoursAndMinutes(hour - 1) }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Select v-model="timeCampaignModel.minute">
                      <SelectTrigger class="w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="minute in 60" :key="minute" :value="minute - 1">
                          {{ formatHoursAndMinutes(minute - 1) }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div class="mt-4">
                <p v-if="isDateSelectedBeforeNow" class="mb-2 text-left text-sm text-red-700">
                  {{ t('pages.campaign.campaignCreatePage.block.chooseDate.invalidDate') }}
                </p>
              </div>
            </fieldset>
          </div>
        </div>
        <div class="col-span-1 mt-3 flex items-center justify-center text-center lg:col-span-3 lg:mt-0 lg:justify-end">
          <BaseConfirmButton
            :title="t('pages.campaign.campaignCreatePage.confirm.modal.title')"
            :text="confirmButtonText"
            :confirm-button-label="t('pages.campaign.campaignCreatePage.confirm.modal.confirm')"
            :cancel-button-label="t('pages.campaign.campaignCreatePage.confirm.modal.cancel')"
            :white="false"
            has-icon
            :disabled="launchDisabled"
            :is-openable="dateCampaignModel !== undefined"
            :processing="isProcessing"
            @confirm="handleCreation"
            @click="calculateLaunchDate"
          >
            <div class="flex items-center">
              <BaseIcon v-if="launchChoice === 'later'" name="HeroClockIconSolid" class="mr-2 h-6 w-6" />
              <BaseIcon v-else name="HeroPaperAirplaneIconSolid" class="mr-2 h-6 w-6" />
              <p>
                {{ t(`pages.campaign.campaignCreatePage.buttons.${launchChoice === 'later' ? 'schedule' : 'launch'}`) }}
              </p>
            </div>
          </BaseConfirmButton>
          <span v-if="launchDisabled" class="ml-2 mr-2 flex items-center cursor-pointer">
            <AppTooltip class="ml-1" width="w-80 z-10" tooltip-position="top">
              <Tooltip>
                <TooltipTrigger as-child>
                  <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 text-foreground-secondary" />
                </TooltipTrigger>
                <TooltipContent
                  ><span v-if="selectedClientTypeModel == null">
                    {{ t('pages.campaign.campaignCreatePage.block.billing.youMustSelectClientType') }}
                  </span>
                  <span v-else-if="!isMessageSurveyInRange">
                    {{
                      messageSurvey?.endDate
                        ? t('pages.campaign.campaignCreatePage.block.launch.surveyNotInRangeWithEndDate', {
                            min: dayjs(messageSurvey?.launchDate * 1000).format('DD/MM/YYYY'),
                            max: dayjs(messageSurvey?.endDate * 1000).format('DD/MM/YYYY')
                          })
                        : t('pages.campaign.campaignCreatePage.block.launch.surveyNotInRangeWithoutEndDate', {
                            min: dayjs(messageSurvey?.launchDate * 1000).format('DD/MM/YYYY')
                          })
                    }}
                  </span>
                  <span v-else-if="hasClientError">
                    {{ t('pages.campaign.campaignCreatePage.block.billing.youMustSelectClient') }}
                  </span>
                  <span v-else-if="isDateSelectedBeforeNow">
                    {{ t('pages.campaign.campaignCreatePage.block.chooseDate.invalidDate') }}
                  </span>
                  <span v-else-if="conversionDelayError.length > 0">
                    {{ t('pages.campaign.campaignCreatePage.notifications.create.conversionDelayError') }}
                  </span>
                </TooltipContent>
              </Tooltip>
            </AppTooltip>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
